import { useMemo, useRef, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  ArrowForwardIcon,
  ChatIcon,
  CloseSVGIcon,
  KeyboardArrowDownIcon,
  KeyboardArrowUpIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useUser } from "@js/apps/common/hooks";
import { ModalInstance } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";

import styles from "./bulk-send-sms-modal.module.scss";

export const SMS_SEGMENT_SIZE = 160;

export type BulkSmsRecipient = {
  freelancerId: number;
  publicName: string | null;
  hasPhone: boolean;
  hasValidPhone: boolean;
  hasConsent: boolean;
};

export type BulkSendSmsResult = {
  freelancer_id: number;
  success: boolean;
  error: string | null;
};

export type BulkSendSmsIneligibilityReason =
  "no_phone" | "not_consented" | "invalid_number";

type BulkSendSmsModalProps = {
  recipients: BulkSmsRecipient[];
  jobUrl?: string;
  onSend: (params: {
    message: string;
    freelancerIds: number[];
    jobUrl?: string;
  }) => Promise<BulkSendSmsResult[]>;
};

const NAME_VARIABLES = [
  { key: "{first_name}", label: "First name" },
  { key: "{public_name}", label: "Full name" },
] as const;

const REASON_LABELS: Record<BulkSendSmsIneligibilityReason, string> = {
  no_phone: "No phone",
  not_consented: "Not opted in",
  invalid_number: "Invalid number",
};

const getInitials = (name: string | null) => {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ""}${parts[parts.length - 1]?.[0] ?? ""}`;
  }
  return parts[0]?.[0] ?? "?";
};

const RecipientChip = ({ publicName }: { publicName: string | null }) => {
  return (
    <Tooltip title={publicName ?? "N/A"} placement="top">
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: "var(--grey-4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <Typography
          component="span"
          variant="paragraph"
          size="small"
          fontWeight={500}
        >
          {getInitials(publicName)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const PREVIEW_SAMPLE_FULL_NAME = "Alex Example";

const buildPreviewMessage = ({
  message,
  senderName,
  sampleName,
  includeJobLink,
  jobUrl,
}: {
  message: string;
  senderName: string;
  sampleName: string | null;
  includeJobLink: boolean;
  jobUrl?: string;
}) => {
  const resolvedSampleName = sampleName?.trim() || PREVIEW_SAMPLE_FULL_NAME;
  const firstName =
    resolvedSampleName.split(" ")[0] || PREVIEW_SAMPLE_FULL_NAME;
  const resolved = message
    .replaceAll("{first_name}", firstName)
    .replaceAll("{public_name}", resolvedSampleName);
  const composed = `${senderName} via Braintrust: ${resolved}`;
  if (includeJobLink && jobUrl) {
    return `${composed} ${jobUrl}`;
  }
  return composed;
};

export const BulkSendSmsModal = ({
  recipients,
  jobUrl,
  onSend,
}: BulkSendSmsModalProps) => {
  const user = useUser();
  const senderName = user?.public_name || user?.first_name || "Braintrust";
  const [message, setMessage] = useState("");
  const [includeJobLink, setIncludeJobLink] = useState(false);
  const [ineligibleExpanded, setIneligibleExpanded] = useState(true);
  const [sendErrors, setSendErrors] = useState<BulkSendSmsResult[]>([]);
  const [isSending, setIsSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { eligible, ineligibleByReason } = useMemo(() => {
    const eligibleRecipients: BulkSmsRecipient[] = [];
    const byReason: Record<BulkSendSmsIneligibilityReason, BulkSmsRecipient[]> =
      {
        no_phone: [],
        not_consented: [],
        invalid_number: [],
      };

    for (const recipient of recipients) {
      if (!recipient.hasPhone) {
        byReason.no_phone.push(recipient);
      } else if (!recipient.hasValidPhone) {
        byReason.invalid_number.push(recipient);
      } else if (!recipient.hasConsent) {
        byReason.not_consented.push(recipient);
      } else {
        eligibleRecipients.push(recipient);
      }
    }

    return { eligible: eligibleRecipients, ineligibleByReason: byReason };
  }, [recipients]);

  const ineligibleCount =
    ineligibleByReason.no_phone.length +
    ineligibleByReason.not_consented.length +
    ineligibleByReason.invalid_number.length;

  const publicNameByFreelancerId = useMemo(
    () =>
      new Map(
        recipients.map((recipient) => [
          recipient.freelancerId,
          recipient.publicName,
        ]),
      ),
    [recipients],
  );

  const previewSampleName =
    eligible[0]?.publicName ??
    recipients.find((recipient) => recipient.publicName)?.publicName ??
    null;

  const previewMessage = buildPreviewMessage({
    message,
    senderName,
    sampleName: previewSampleName,
    includeJobLink,
    jobUrl,
  });

  const segmentCount = Math.max(
    1,
    Math.ceil(previewMessage.length / SMS_SEGMENT_SIZE),
  );
  const segmentMax = segmentCount * SMS_SEGMENT_SIZE;

  const handleInsertVariable = (variableKey: string) => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? message.length;
    const end = textarea?.selectionEnd ?? message.length;
    const newMessage =
      message.slice(0, start) + variableKey + message.slice(end);

    setMessage(newMessage);

    const cursorPosition = start + variableKey.length;
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  const handleSend = async () => {
    setSendErrors([]);
    setIsSending(true);

    try {
      const results = await onSend({
        message,
        freelancerIds: eligible.map((recipient) => recipient.freelancerId),
        jobUrl: includeJobLink && jobUrl ? jobUrl : undefined,
      });

      const failures = results.filter((result) => !result.success);
      const successCount = results.length - failures.length;

      if (successCount > 0) {
        Snackbar.success(
          `SMS sent to ${successCount} of ${eligible.length} eligible talent.`,
        );
      }

      if (failures.length > 0) {
        setSendErrors(failures);
        Snackbar.error(`Failed to send SMS to ${failures.length} talent.`);
      } else {
        ModalInstance.close();
      }
    } catch {
      Snackbar.error("Failed to send SMS. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Stack sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" sx={{ mb: 2.5, gap: 2, alignItems: "start" }}>
        <Box
          sx={{
            mt: "2px",
            width: 48,
            height: 48,
            borderRadius: "12px",
            backgroundColor: "var(--grey-4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <ChatIcon sx={{ color: "var(--grey-1)" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            component="h2"
            variant="paragraph"
            size="large"
            fontWeight={500}
            sx={{ lineHeight: "1.4 !important" }}
          >
            Send SMS
          </Typography>
          <Typography
            component="p"
            variant="paragraph"
            color="secondary"
            size="small"
          >
            {eligible.length} of {recipients.length} will receive
          </Typography>
        </Box>
        <IconButton
          aria-label="close modal"
          variant="transparent"
          onClick={() => ModalInstance.close()}
          sx={{
            borderRadius: "50% !important",
            backgroundColor: "var(--grey-4)",
            flexShrink: 0,
          }}
        >
          <CloseSVGIcon />
        </IconButton>
      </Stack>

      {eligible.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
            {eligible.map((recipient) => (
              <RecipientChip
                key={recipient.freelancerId}
                publicName={recipient.publicName}
              />
            ))}
          </Stack>
        </Box>
      )}

      {ineligibleCount > 0 && (
        <Box
          sx={{
            backgroundColor: "var(--soft-red)",
            borderRadius: "8px",
            p: 2,
            mb: 2.5,
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
            }}
            onClick={() => setIneligibleExpanded((expanded) => !expanded)}
          >
            <Typography
              component="p"
              variant="paragraph"
              size="small"
              fontWeight={500}
            >
              {ineligibleCount} won&apos;t receive
            </Typography>
            {ineligibleExpanded ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </Stack>

          {ineligibleExpanded && (
            <Stack
              sx={{ gap: 1.5, mt: 1.5, maxHeight: 250, overflowY: "auto" }}
            >
              {(
                Object.entries(ineligibleByReason) as [
                  BulkSendSmsIneligibilityReason,
                  BulkSmsRecipient[],
                ][]
              )
                .filter(([, group]) => group.length > 0)
                .map(([reason, group]) => (
                  <Box key={reason}>
                    <Typography
                      component="p"
                      variant="paragraph"
                      size="small"
                      color="secondary"
                      sx={{ mb: 0.75 }}
                    >
                      {REASON_LABELS[reason]} ({group.length})
                    </Typography>
                    <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
                      {group.map((recipient) => (
                        <RecipientChip
                          key={recipient.freelancerId}
                          publicName={recipient.publicName}
                        />
                      ))}
                    </Stack>
                  </Box>
                ))}
            </Stack>
          )}
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: "var(--grey-5)",
          borderRadius: "8px",
          p: 2,
          mb: 2.5,
        }}
      >
        <Typography
          component="p"
          variant="paragraph"
          size="small"
          fontWeight={500}
          sx={{ mb: 1 }}
        >
          Insert variable:
        </Typography>
        <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
          {NAME_VARIABLES.map((variable) => (
            <Button
              key={variable.key}
              variant="secondary"
              shape="squared"
              size="x-small"
              onClick={() => handleInsertVariable(variable.key)}
            >
              + {variable.label}
            </Button>
          ))}
        </Stack>
      </Box>

      <TextField
        multiline
        id="bulk-sms-message"
        rows={5}
        fullWidth
        placeholder="Type a text message..."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        inputRef={textareaRef}
        sx={{ mb: 1, textarea: { p: "0 !important" } }}
        slotProps={{ htmlInput: { maxLength: 1600 } }}
      />

      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography
          component="span"
          variant="paragraph"
          size="small"
          color="secondary"
        >
          {segmentCount} SMS segment{segmentCount !== 1 ? "s" : ""} ·{" "}
          {eligible.length} recipient{eligible.length !== 1 ? "s" : ""}
        </Typography>
        <Typography
          component="span"
          variant="paragraph"
          size="small"
          color="secondary"
        >
          {previewMessage.length} / {segmentMax}
        </Typography>
      </Stack>

      {!!jobUrl && (
        <Box sx={{ mb: 1.5 }}>
          <Checkbox
            checked={includeJobLink}
            onChange={(event) => setIncludeJobLink(event.target.checked)}
            size="small"
            label={
              <Typography component="span" variant="paragraph" size="small">
                Include job link
              </Typography>
            }
          />
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: "var(--grey-5)",
          borderRadius: "12px",
          p: 2,
          mb: 2.5,
        }}
      >
        <Typography
          component="p"
          variant="paragraph"
          size="small"
          color="secondary"
          sx={{ mb: 0.75 }}
        >
          Preview
        </Typography>
        <Typography
          component="p"
          variant="paragraph"
          size="small"
          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {previewMessage || `${senderName} via Braintrust: `}
        </Typography>
      </Box>

      {sendErrors.length > 0 && (
        <Box
          sx={{
            backgroundColor: "var(--soft-red)",
            borderRadius: "8px",
            p: 2,
            mb: 2.5,
          }}
        >
          <Typography
            component="p"
            variant="paragraph"
            size="small"
            fontWeight={500}
            sx={{ mb: 1 }}
          >
            Failed to send ({sendErrors.length}):
          </Typography>
          <Stack sx={{ gap: 0.5, maxHeight: 320, overflowY: "auto" }}>
            {sendErrors.map((error) => (
              <Typography
                key={error.freelancer_id}
                component="p"
                variant="paragraph"
                size="small"
              >
                <b>
                  {publicNameByFreelancerId.get(error.freelancer_id) ??
                    "Unknown talent"}
                  :
                </b>{" "}
                {error.error ?? "Failed to send SMS."}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      <Stack direction="row" sx={{ justifyContent: "flex-end", gap: 1 }}>
        <Button
          variant="secondary"
          shape="squared"
          onClick={() => ModalInstance.close()}
          disabled={isSending}
        >
          Cancel
        </Button>
        <Button
          variant="positive"
          shape="squared"
          onClick={handleSend}
          disabled={!message.trim() || isSending || eligible.length === 0}
          endIcon={<ArrowForwardIcon />}
        >
          {isSending ? "Sending..." : `Send SMS to ${eligible.length}`}
        </Button>
      </Stack>
    </Stack>
  );
};

export const openBulkSendSmsModal = (props: BulkSendSmsModalProps) => {
  ModalInstance.open({
    children: <BulkSendSmsModal {...props} />,
    padding: false,
    closeButton: false,
    className: styles.modal,
  });
};
